import { useEffect, useMemo, useState } from 'react';
import { actionType, initialAddressState, showActionType } from 'config/constants';
import {
  Box,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContentText,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { useAddresses, useAddressesFunctions } from 'contexts/AddressContext';
import { useFormik } from 'formik';
import { usePreferencesFunctions } from 'contexts/PreferencesContext';
import * as yup from 'yup';
import AddCircleOutlineRoundedIcon from '@material-ui/icons/AddCircleOutlineRounded';
import AddressForm from 'components/AddressForm';
import clsx from 'clsx';
import DefaultButton from 'components/DefaultButton';
import DefaultModal from 'components/DefaultModal';
import useStyles from './styles';

const selectActionValidationSchema = yup
  .object({
    selectedAction: yup
      .string()
      .oneOf(['DEACTIVATE', 'DELETE', 'TRANSFER'], 'Veuillez choisir une valeur')
      .required('Veuillez choisir une valeur'),
  })
  .defined();

const selectAddressValidationSchema = yup
  .object({
    selectedAddress: yup.mixed().required('Veuillez choisir une adresse'),
  })
  .defined();

const skipWarningValidationSchema = yup
  .object({
    skipWarning: yup.bool(),
  })
  .defined();

const texts = {
  deactivate: {
    title: 'Désactivation d’un lieu',
    subTitle: 'Attention vous allez désactiver un lieu de prestation !',
  },
  delete: {
    title: 'Suppression d’un lieu',
    subTitle: 'Attention vous allez supprimer un lieu de prestation !',
  },
};

const getTitle = (modalType, showAction) => {
  switch (showAction) {
    case showActionType.CONFLICT_SELECT:
      return 'Attention';
    case showActionType.CONFLICT_TRANSFER:
      return 'Conflit de lieu';
    case showActionType.SELECT_ACTION:
      return 'Que voulez-vous faire des prestations sur ce lieu ?';
    case showActionType.SELECT_ADDRESS:
      return 'Choisissez un lieu pour vos prestations';
    default:
      return texts[modalType].title;
  }
};

const getValidationSchema = (showAction) => {
  switch (showAction) {
    case showActionType.CONFLICT_SELECT:
      return null;
    case showActionType.CONFLICT_TRANSFER:
      return null;
    case showActionType.SELECT_ACTION:
      return selectActionValidationSchema;
    case showActionType.SELECT_ADDRESS:
      return selectAddressValidationSchema;
    default:
      return skipWarningValidationSchema;
  }
};

function ActionOnAddressModal({ address, loading, onClose, onConfirm, showAction, modalType }) {
  const classes = useStyles();
  const { addresses } = useAddresses();
  const { createAddress } = useAddressesFunctions();
  const { changePreference } = usePreferencesFunctions();

  const enabledAddresses = useMemo(
    () => addresses.filter((a) => a.enabled && a.id !== address?.id),
    [address?.id, addresses],
  );
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [createAddressModalOpen, setCreateAddressModalOpen] = useState(false);

  const { handleChange, handleSubmit, resetForm, setFieldValue, values } = useFormik({
    initialValues: {
      selectedAction: '',
      selectedAddress: null,
      skipWarning: false,
    },
    validationSchema: getValidationSchema(showAction),
    onSubmit: (values) => {
      if (values.skipWarning) {
        changePreference(`show_${modalType}_address_warning`, false);
      }

      onConfirm?.(values);
    },
  });

  useEffect(() => {
    resetForm();
    setSelectedAddressId(null);
  }, [resetForm, showAction]);

  const handleCancelConflictDeleteOrDeactivate = () => {
    onConfirm({ forceAction: false });
  };

  const handleConflictDeleteOrDeactivateSubmit = () => {
    onConfirm({ forceAction: true });
  };

  const handleChangeAddress = (value) => {
    const id = parseInt(value, 10);
    setSelectedAddressId(id);
    setFieldValue(
      'selectedAddress',
      enabledAddresses.find((a) => a.id === id),
    );
  };

  const handleCreateAddress = async (values) => {
    const body = {
      address: values.address_name,
      city: values.city_name,
      title: values.title,
      index: values.street_index,
      number: values.street_number,
      zipCode: values.zip_code,
    };

    const { created } = await createAddress(body);
    if (created) setCreateAddressModalOpen(false);
  };

  const ConflictSelectAction = () => (
    <Grid container item xs={12}>
      <DialogContentText className={clsx(upperTextClasses, classes.primaryText, classes.modalText)}>
        {`Certaines prestations ont déjà été réservées, les activités concernées seront bloquées${
          modalType === actionType.DELETE ? ' et cette adresse ne sera pas supprimée' : ''
        }.`}
      </DialogContentText>
    </Grid>
  );

  const ConflictTransfer = () => (
    <Grid container item xs={12} justifyContent="center">
      <DialogContentText
        className={clsx(upperTextClasses, classes.primaryText, classes.modalText)}
        style={{ textAlign: 'justify' }}
      >
        Nous avons repéré que ce changement de lieu entraine un conflit avec d’autres prestations sur le même lieu au
        même moment.
      </DialogContentText>

      <Typography align="center" className={classes.conflictQuestion}>
        Que voulez-vous faire ?
      </Typography>

      <DialogActions>
        <DefaultButton disabled={loading} standard onClick={() => onConfirm({ forceTransfer: true })}>
          {loading ? <CircularProgress size={20} /> : 'Attribuer'}
        </DefaultButton>
        <DefaultButton disabled={loading} standard onClick={() => onConfirm({ forceTransfer: false })}>
          Changer
        </DefaultButton>
      </DialogActions>
    </Grid>
  );

  const SelectActionForm = () => (
    <>
      <FormControl className={classes.formControl} component="fieldset">
        <RadioGroup
          aria-label="selectedAction"
          className={classes.radioGroup}
          name="selectedAction"
          value={values.selectedAction}
          onChange={handleChange}
        >
          <Box className={classes.radio}>
            <Radio color="primary" value="DEACTIVATE" />
            <Typography>Désactiver les prestations</Typography>
          </Box>
          <Box className={classes.radio}>
            <Radio color="primary" value="DELETE" />
            <Typography>Supprimer les prestations</Typography>
          </Box>
          <Box className={classes.radio}>
            <Radio color="primary" value="TRANSFER" />
            <Typography>Transférer les prestations vers un autre lieu</Typography>
          </Box>
        </RadioGroup>
      </FormControl>
    </>
  );

  const SelectAddressForm = () => (
    <Box className={classes.addressForm}>
      <FormControl
        className={clsx(classes.formControl, classes.addressRadio)}
        component="fieldset"
        style={{ flexGrow: 1 }}
      >
        <RadioGroup
          aria-label="selectedAddress"
          value={selectedAddressId}
          onChange={(_, value) => handleChangeAddress(value)}
        >
          {enabledAddresses.map((address) => (
            <Box className={classes.radio}>
              <Radio color="primary" value={address.id} />
              <Typography>{address.title}</Typography>
            </Box>
          ))}
        </RadioGroup>
      </FormControl>

      <Box className={classes.buttonContainer}>
        <DefaultButton
          className={classes.button}
          startIcon={<AddCircleOutlineRoundedIcon />}
          onClick={() => setCreateAddressModalOpen(true)}
        >
          Créer un nouveau lieu
        </DefaultButton>
      </Box>
    </Box>
  );

  const WarningForm = () => (
    <>
      <Grid container item xs={12} justify="center">
        <DialogContentText className={clsx(upperTextClasses, classes.primaryText, classes.modalText)}>
          {texts[modalType].subTitle}
        </DialogContentText>
      </Grid>

      <FormControlLabel
        className={classes.preferenceCheckbox}
        name="skipWarning"
        control={<Checkbox color="primary" name="skipWarning" checked={values.skipWarning} onChange={handleChange} />}
        label="Ne plus afficher cet avertissement"
      />
    </>
  );

  let upperTextClasses = classes.biggerModalText;
  if (showAction === showActionType.SHOULD_TRANSFER) upperTextClasses = classes.lesserModalText;

  const close = showAction === showActionType.CONFLICT_SELECT ? handleCancelConflictDeleteOrDeactivate : onClose;
  const title = getTitle(modalType, showAction);
  const submit = showAction === showActionType.CONFLICT_SELECT ? handleConflictDeleteOrDeactivateSubmit : handleSubmit;
  return (
    <>
      <DefaultModal
        className={classes.modal}
        forceConfirm
        hideActions={showAction === showActionType.CONFLICT_TRANSFER}
        loading={loading}
        open={!!showAction}
        title={title}
        onClose={close}
        onConfirm={submit}
        square
      >
        {showAction === showActionType.CONFLICT_SELECT && <ConflictSelectAction />}
        {showAction === showActionType.CONFLICT_TRANSFER && <ConflictTransfer />}
        {showAction === showActionType.PREFERENCE_WARNING && <WarningForm />}
        {showAction === showActionType.SELECT_ACTION && <SelectActionForm />}
        {showAction === showActionType.SELECT_ADDRESS && <SelectAddressForm />}
      </DefaultModal>

      <DefaultModal hideActions maxWidth="md" open={createAddressModalOpen} title="Création d'un lieu">
        <AddressForm
          id="addressForm-actionOnAddress"
          address={initialAddressState}
          dialog
          onCancel={() => setCreateAddressModalOpen(false)}
          onSubmit={handleCreateAddress}
        />
      </DefaultModal>
    </>
  );
}

export default ActionOnAddressModal;
